import { FC, ReactNode, useEffect, useState } from 'react';
import { Divider, Segmented } from 'antd';
import { TbStack3 } from 'react-icons/tb';

import { useReportComponentStore } from '@/stores';
import { ReportComponent, ReportComponentType } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

interface ChartFormattingProps {
    component: ReportComponent;
}

type ChartLayout = 'horizontal' | 'vertical';
type BarGroupMode = 'grouped' | 'stacked';

export const hasChartFormattingControls = (type: ReportComponentType) =>
    [ReportComponentType.BAR_CHART, ReportComponentType.FUNNEL_CHART].includes(type);

const ChartFormatting: FC<ChartFormattingProps> = ({ component }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);
    const updateDraft = useReportComponentStore((s) => s.updateDraftConfiguration);

    const draft: Partial<ReportComponentConfig> = draftConfigurations[component.id];

    const map = {
        [ReportComponentType.FUNNEL_CHART]: (
            <>
                <div style={{ marginBottom: '1rem' }}>
                    <Segmented
                        options={[
                            { value: 'vertical', label: 'Vertical' },
                            { value: 'horizontal', label: 'Horizontal' },
                        ]}
                        value={draft?.formatting?.layout ?? 'vertical'}
                        onChange={(v) =>
                            updateDraft(component.id, {
                                ...draft,
                                formatting: {
                                    ...draft?.formatting,
                                    layout: v as ChartLayout,
                                },
                            })
                        }
                        size='small'
                    />
                </div>
            </>
        ),
        [ReportComponentType.BAR_CHART]: (
            <>
                <Segmented
                    options={[
                        {
                            value: 'grouped',
                            label: (
                                <>
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='1rem'
                                        height='1rem'
                                        style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }}
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-chart-column'
                                    >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M4 20h3' />
                                        <path d='M17 20h3' />
                                        <path d='M10.5 20h3' />
                                        <path d='M4 16h3' />
                                        <path d='M17 16h3' />
                                        <path d='M10.5 16h3' />
                                        <path d='M4 12h3' />
                                        <path d='M17 12h3' />
                                        <path d='M10.5 12h3' />
                                        <path d='M4 8h3' />
                                        <path d='M17 8h3' />
                                        <path d='M4 4h3' />
                                    </svg>
                                    Grouped
                                </>
                            ),
                        },
                        {
                            value: 'stacked',
                            label: (
                                <>
                                    <TbStack3
                                        size='1rem'
                                        style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }}
                                    />
                                    Stacked
                                </>
                            ),
                        },
                    ]}
                    value={draft?.formatting?.groupBy ?? 'grouped'}
                    onChange={(v) =>
                        updateDraft(component.id, {
                            ...draft,
                            formatting: {
                                ...draft?.formatting,
                                groupBy: v as BarGroupMode,
                            },
                        })
                    }
                    size='small'
                    style={{ marginBottom: '1.5rem' }}
                />
                <Divider type='vertical' />
                <Segmented
                    options={[
                        {
                            value: 'vertical',
                            label: (
                                <>
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='1rem'
                                        height='1rem'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-up-dashed'
                                        style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }}
                                    >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M12 5v6m0 3v1.5m0 3v.5' />
                                        <path d='M18 11l-6 -6' />
                                        <path d='M6 11l6 -6' />
                                    </svg>
                                    Vertical
                                </>
                            ),
                        },
                        {
                            value: 'horizontal',
                            label: (
                                <>
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='1rem'
                                        height='1rem'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-right-dashed'
                                        style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }}
                                    >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M5 12h.5m3 0h1.5m3 0h6' />
                                        <path d='M13 18l6 -6' />
                                        <path d='M13 6l6 6' />
                                    </svg>
                                    Horizontal
                                </>
                            ),
                        },
                    ]}
                    value={draft?.formatting?.layout ?? 'vertical'}
                    onChange={(v) =>
                        updateDraft(component.id, {
                            ...draft,
                            formatting: {
                                ...draft?.formatting,
                                layout: v as ChartLayout,
                            },
                        })
                    }
                    size='small'
                    style={{ marginBottom: '1.5rem' }}
                />
            </>
        ),
    } as Record<ReportComponentType, ReactNode>;

    return <>{map[component?.type] ?? null}</>;
};

export default ChartFormatting;
